/**
 * Admin · Background segmenter (super-admin, spec 022) — calibrate the on-device matte
 * ONCE for the whole platform. The model choice (BiRefNet by default) + saliency post-
 * processing (gain / gamma / black-point) + edge refine (choke / spread / feather /
 * despill) are stored platform-globally (NULL-tenant settings row) and ride the settings
 * warm-up cache, so EVERY customer + studio editor reads exactly this — no per-client
 * upload. Saving writes the fresh value straight into the cache + applies the model
 * locally, so the change is live immediately for this session and inherited by others on
 * their next warm-up.
 */
import { useCallback, useEffect, useState } from "react";
import { ActionBar } from "../ui/ActionBar.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint } from "../ui/Tooltip.js";
import { SEGMENTATION_HELP, valueHelpText, type ValueHelp } from "../ui/valueHelp.js";
import { useServices } from "../app/context.js";
import { LightPreview } from "./LightPreview.js";
import { RekeyAll } from "./RekeyAll.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import { CACHE_KEYS } from "../stores/sessionWarmup.js";
import {
  CALIBRATION_DEFAULTS, KEY_DEPTH_MAX, KEY_DEPTH_MIN, applySegModelFromCalibration, resolveCalibration,
} from "../stores/segmenterCalibration.js";
import { isModelCachedById, downloadModelById, formatSpeed, formatEta, type DownloadProgress } from "../stores/modelStore.js";
import type { SegmentationCalibration, WorkspaceSettings } from "@genz/platform";

type Calib = Required<SegmentationCalibration>;

/** Merge whatever the server returned over the engine defaults → a fully-populated form. */
function fromSettings(s: WorkspaceSettings | null): Calib {
  return { ...CALIBRATION_DEFAULTS, ...(s?.segmenter_calibration ?? {}) };
}

/** A labelled range slider that surfaces its current numeric value (super-admin clarity). */
function Slider(props: {
  id: string; label: string; help: ValueHelp;
  min: number; max: number; step: number;
  value: number; fmt?: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const { id, label, help, min, max, step, value, fmt, onChange } = props;
  return (
    <div className="setting-row">
      <div>
        <span className="setting-row__label">{label}</span>{" "}<span data-testid={`${id}-help`}><InfoHint label={`About ${label}`} text={valueHelpText(help)} /></span>
      </div>
      <div className="setting-row__control">
        <input
          type="range" data-testid={id} min={min} max={max} step={step} value={value}
          aria-label={label} className="seg-calib__slider"
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span data-testid={`${id}-val`} className="faint seg-calib__value">
          {(fmt ?? String)(value)}
        </span>
      </div>
    </div>
  );
}

export function SegmentationTab() {
  const { client } = useServices();
  // Cache-first: seed from the cached workspace settings (shared with the editor's
  // resolveCalibration) so the tab shows instantly; the fetch below revalidates.
  const [c, setC] = useState<Calib>(() => fromSettings(readCache<WorkspaceSettings>(CACHE_KEYS.settings)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** Whether the SELECTED model is already on this device (null = checking). */
  const [modelCached, setModelCached] = useState<boolean | null>(null);
  /** Live download progress for the Download button; null when idle. */
  const [dlProgress, setDlProgress] = useState<DownloadProgress | null>(null);
  const [dling, setDling] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const epoch = cacheEpoch();
    try {
      const fresh = await client.getSettings();
      writeCache(CACHE_KEYS.settings, fresh, epoch);
      setC(fromSettings(fresh));
    } catch (e) {
      if (readCache<WorkspaceSettings>(CACHE_KEYS.settings) == null) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  // Reflect whether the SELECTED model is downloaded to this device (re-checks on change).
  useEffect(() => {
    let cancelled = false;
    setModelCached(null);
    void isModelCachedById(c.model).then((ok) => { if (!cancelled) setModelCached(ok); });
    return () => { cancelled = true; };
  }, [c.model]);

  /** Download the selected model to the device now, with a live progress bar. */
  async function downloadModel() {
    if (dling) return;
    setError(null);
    setDling(true);
    setDlProgress({ loaded: 0, total: 0, pct: 0, bytesPerSec: 0, etaSec: 0 });
    try {
      const ok = await downloadModelById(c.model, setDlProgress);
      setModelCached(ok);
      if (!ok) setError("Model download failed — check the connection and try again.");
    } finally {
      setDling(false);
      setDlProgress(null);
    }
  }

  // `key_by_purity` is how v0.2.129 saved the choice, before there were three methods.
  const method = c.key_by_purity ? "purity" : c.key_method;

  function set<K extends keyof Calib>(key: K, value: Calib[K]) {
    setC((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function reset() {
    setC({ ...CALIBRATION_DEFAULTS });
    setSaved(false);
  }

  async function save() {
    setBusy(true);
    setError(null);
    const epoch = cacheEpoch();
    try {
      // "Apply to every template" is an action: each save with it on raises the count, and a
      // template saved at an older count takes these values until it is saved again.
      const calibration = c.key_depth_all_templates ? { ...c, light_revision: resolveCalibration().light_revision + 1 } : c;
      const updated = await client.updateSettings({ segmenter_calibration: calibration });
      // The server clamps + echoes the platform-global value back; trust it and push it
      // into the cache so this session's editor (resolveCalibration) + every surface that
      // re-warms inherits it. Apply the model choice locally right away.
      writeCache(CACHE_KEYS.settings, updated, epoch);
      setC(fromSettings(updated));
      applySegModelFromCalibration();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-tab seg-layout" data-testid="admin-segmenter">
      {/* Owner, 2026-10-14: the preview on the left, in view while the values on the right move. */}
      <aside className="seg-layout__preview">
        <LightPreview values={c} />
      </aside>
      <div className="admin-panel">
        <div className="admin-panel__head admin-panel__head--flush">
          <div>
            <strong className="admin-panel__title panel-title">BiRefNet matte calibration <InfoHint text="Tunes the on-device AI background matte (BiRefNet) used by the editor’s “Remove background (AI)” for every customer + studio. Set once; it applies platform-wide." /></strong>
          </div>
        </div>
        {error && <p role="alert" data-testid="segmenter-error">{error}</p>}

        <div data-testid="segmenter-form">
          {/* Model + on-device download. The matte model is fetched to the device on demand;
              show whether it's present and let the admin download it here with a progress bar
              (rather than relying on the app Update flow). */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Model</span>{" "}<span data-testid="seg-model-help"><InfoHint label="About Model" text={valueHelpText(SEGMENTATION_HELP.model)} /></span>
            </div>
            <div className="setting-row__control seg-model__control">
              <div className="seg-model__row">
                <select
                  data-testid="seg-model" value={c.model} aria-label="Segmenter model"
                  onChange={(e) => set("model", e.target.value as Calib["model"])}
                >
                  <option value="birefnet">BiRefNet lite (recommended)</option>
                  <option value="birefnet_full">BiRefNet full (HQ · ~489 MB)</option>
                  <option value="u2netp">U²-Net-p</option>
                </select>
                <span
                  data-testid="seg-model-status"
                  className={`seg-model__state ${modelCached ? "seg-model__state--cached" : "seg-model__state--absent"}`}
                >
                  {modelCached == null ? "checking…" : modelCached ? <><Icon name="check" size={16} /> Downloaded</> : "Not downloaded"}
                </span>
                <button
                  type="button"
                  className="btn btn--sm"
                  data-testid="seg-model-download"
                  disabled={dling || modelCached === true}
                  onClick={() => void downloadModel()}
                  title="Download this model to the device now, with progress"
                >
                  {dling ? "Downloading…" : modelCached ? "Downloaded" : <><Icon name="download" size={16} /> Download</>}
                </button>
              </div>
              {dlProgress && (
                <div data-testid="seg-model-progress" className="seg-model__progress">
                  <div className="seg-model__bar">
                    {/* The WIDTH is data — a live download percentage — so it stays inline; everything
                        else about the bar is in `admin.css`. */}
                    <div className="seg-model__fill" style={{ width: `${Math.round(dlProgress.pct * 100)}%` }} />
                  </div>
                  <span>
                    {Math.round(dlProgress.pct * 100)}%
                    {dlProgress.bytesPerSec > 0 ? ` · ${formatSpeed(dlProgress.bytesPerSec)}` : ""}
                    {dlProgress.etaSec > 0 ? ` · ~${formatEta(dlProgress.etaSec)} left` : ""}
                  </span>
                </div>
              )}
            </div>
          </div>

          <Slider id="seg-gain" label="Saliency gain" help={SEGMENTATION_HELP.mask_gain}
            min={0.5} max={2.0} step={0.05} value={c.mask_gain} fmt={(v) => `${v.toFixed(2)}×`}
            onChange={(v) => set("mask_gain", v)} />

          <Slider id="seg-gamma" label="Saliency gamma" help={SEGMENTATION_HELP.mask_gamma}
            min={0.5} max={2.0} step={0.05} value={c.mask_gamma} fmt={(v) => v.toFixed(2)}
            onChange={(v) => set("mask_gamma", v)} />

          <Slider id="seg-threshold" label="Black point" help={SEGMENTATION_HELP.mask_threshold}
            min={0} max={255} step={1} value={c.mask_threshold} fmt={(v) => String(Math.round(v))}
            onChange={(v) => set("mask_threshold", v)} />

          <Slider id="seg-choke" label="Edge choke / spread" help={SEGMENTATION_HELP.edge_choke_px}
            min={-5} max={10} step={1}
            value={c.edge_choke_px} fmt={(v) => (v > 0 ? `+${v}px` : v < 0 ? `${v}px` : "0")}
            onChange={(v) => set("edge_choke_px", v)} />

          <Slider id="seg-feather" label="Edge feather" help={SEGMENTATION_HELP.edge_feather_px}
            min={0} max={10} step={1} value={c.edge_feather_px} fmt={(v) => `${Math.round(v)}px`}
            onChange={(v) => set("edge_feather_px", v)} />

          <Slider id="seg-despill" label="Edge despill" help={SEGMENTATION_HELP.despill_amount}
            min={0} max={1} step={0.05} value={c.despill_amount} fmt={(v) => v.toFixed(2)}
            onChange={(v) => set("despill_amount", v)} />

          {/* Owner, 2026-10-12: one calibration, set here, for every customer. A customer has
              no control for it; an admin can still tune one template in the editor. */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Print guide: key method</span>{" "}<span data-testid="key-method-help"><InfoHint label="About Print guide: key method" text={valueHelpText(SEGMENTATION_HELP.key_method)} /></span>
            </div>
            <div className="setting-row__control">
              <select className="select" data-testid="key-method" aria-label="Print guide: key method" value={method}
                onChange={(e) => { setC((prev) => ({ ...prev, key_method: e.target.value as Calib["key_method"], key_by_purity: e.target.value === "purity" })); setSaved(false); }}>
                <option value="bulk">Colour to alpha (as the Transparent tab)</option>
                <option value="purity">Purity</option>
                <option value="spread">Spread</option>
              </select>
            </div>
          </div>

          {method === "bulk" && (
            <>
              <Slider id="key-noise-floor" label="Print guide: noise floor" help={SEGMENTATION_HELP.key_noise_floor}
                min={0} max={20} step={1} value={c.key_noise_floor} onChange={(v) => set("key_noise_floor", v)} />
              <Slider id="key-flat-sample" label="Print guide: flat sample" help={SEGMENTATION_HELP.key_flat_sample}
                min={0.3} max={0.9} step={0.05} value={c.key_flat_sample} fmt={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => set("key_flat_sample", v)} />
              <Slider id="key-snap-low" label="Print guide: transparent snap" help={SEGMENTATION_HELP.key_snap_low}
                min={0} max={0.2} step={0.005} value={c.key_snap_low} fmt={(v) => `${(v * 100).toFixed(1)}%`}
                onChange={(v) => set("key_snap_low", v)} />
              <Slider id="key-snap-high" label="Print guide: opaque snap" help={SEGMENTATION_HELP.key_snap_high}
                min={0.8} max={1} step={0.005} value={c.key_snap_high} fmt={(v) => `${(v * 100).toFixed(1)}%`}
                onChange={(v) => set("key_snap_high", v)} />
            </>
          )}

          {method === "purity" ? (
            <>
              <Slider id="key-purity-start" label="Print guide: keying starts" help={SEGMENTATION_HELP.key_purity_start}
                min={0} max={1} step={0.01} value={c.key_purity_start} fmt={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => set("key_purity_start", v)} />
              <Slider id="key-purity-full" label="Print guide: fully transparent at" help={SEGMENTATION_HELP.key_purity_full}
                min={0} max={1} step={0.01} value={c.key_purity_full} fmt={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => set("key_purity_full", v)} />
              <Slider id="key-dark-keep" label="Print guide: keep dark magenta" help={SEGMENTATION_HELP.key_dark_keep}
                min={0} max={1} step={0.05} value={c.key_dark_keep} fmt={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => set("key_dark_keep", v)} />
            </>
          ) : method === "spread" && (
          <Slider id="key-tolerance" label="Print guide: tolerance" help={SEGMENTATION_HELP.key_tolerance}
            min={0} max={1} step={0.05} value={c.key_tolerance} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("key_tolerance", v)} />
          )}

          <Slider id="key-softness" label="Print guide: edge softness" help={SEGMENTATION_HELP.key_softness}
            min={0} max={4} step={1} value={c.key_softness} fmt={(v) => `${Math.round(v)}px`}
            onChange={(v) => set("key_softness", v)} />

          <Slider id="key-colour-removal" label="Print guide: colour removal" help={SEGMENTATION_HELP.key_colour_removal}
            min={0} max={1} step={0.05} value={c.key_colour_removal} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("key_colour_removal", v)} />

          <Slider id="key-shadow-depth" label="Print guide: shadow depth" help={SEGMENTATION_HELP.key_shadow_depth}
            min={KEY_DEPTH_MIN} max={KEY_DEPTH_MAX} step={0.05} value={c.key_shadow_depth} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("key_shadow_depth", v)} />

          <Slider id="key-highlight-depth" label="Print guide: highlight depth" help={SEGMENTATION_HELP.key_highlight_depth}
            min={KEY_DEPTH_MIN} max={KEY_DEPTH_MAX} step={0.05} value={c.key_highlight_depth} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("key_highlight_depth", v)} />

          <Slider id="print-shading" label="Artwork shading" help={SEGMENTATION_HELP.print_shading}
            min={0} max={1.5} step={0.01} value={c.print_shading} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_shading", v)} />

          <Slider id="print-highlights" label="Highlights" help={SEGMENTATION_HELP.print_highlights}
            min={0} max={1} step={0.01} value={c.print_highlights} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_highlights", v)} />

          <Slider id="print-gradient" label="Light gradient" help={SEGMENTATION_HELP.print_gradient}
            min={0} max={1.5} step={0.01} value={c.print_gradient} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_gradient", v)} />

          <Slider id="print-grain" label="Surface grain" help={SEGMENTATION_HELP.print_grain}
            min={0} max={1} step={0.01} value={c.print_grain} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_grain", v)} />

          <Slider id="print-sheen" label="Gloss sheen" help={SEGMENTATION_HELP.print_sheen}
            min={0} max={2} step={0.01} value={c.print_sheen} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_sheen", v)} />

          <Slider id="print-black" label="Darkest black" help={SEGMENTATION_HELP.print_black}
            min={0} max={0.5} step={0.01} value={c.print_black} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_black", v)} />

          <Slider id="print-white" label="Brightest white" help={SEGMENTATION_HELP.print_white}
            min={0.5} max={1} step={0.01} value={c.print_white} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("print_white", v)} />

          <Slider id="shading-smoothing" label="Shading smoothing" help={SEGMENTATION_HELP.shading_smoothing}
            min={0} max={1} step={0.05} value={c.shading_smoothing} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("shading_smoothing", v)} />

          <Slider id="case-highlights" label="Case highlights" help={SEGMENTATION_HELP.case_highlights}
            min={0} max={1} step={0.05} value={c.case_highlights} fmt={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => set("case_highlights", v)} />

          <RekeyAll keyOptions={{
            method: method, tolerance: c.key_tolerance, start: c.key_purity_start, full: c.key_purity_full,
            darkKeep: c.key_dark_keep, noiseFloor: c.key_noise_floor, snapLow: c.key_snap_low,
            snapHigh: c.key_snap_high, flatSample: c.key_flat_sample, softness: c.key_softness,
            colour: c.key_colour_removal,
          }} />

          <div className="setting-row">
            <div>
              <span className="setting-row__label">Flatten the magenta</span>{" "}<span data-testid="flatten-magenta-help"><InfoHint label="About Flatten the magenta" text={valueHelpText(SEGMENTATION_HELP.flatten_magenta)} /></span>
            </div>
            <div className="setting-row__control">
              <input type="checkbox" data-testid="flatten-magenta" checked={c.flatten_magenta}
                aria-label="Flatten the magenta" onChange={(e) => set("flatten_magenta", e.target.checked)} />
            </div>
          </div>

          <div className="setting-row">
            <div>
              <span className="setting-row__label">Apply these values to every template</span>{" "}<span data-testid="key-depth-all-templates-help"><InfoHint label="About Apply these values to every template" text={valueHelpText(SEGMENTATION_HELP.key_depth_all_templates)} /></span>
            </div>
            <div className="setting-row__control">
              <input type="checkbox" data-testid="key-depth-all-templates" checked={c.key_depth_all_templates}
                aria-label="Apply these values to every template" onChange={(e) => set("key_depth_all_templates", e.target.checked)} />
            </div>
          </div>

          <div className="setting-row">
            <div>
              <span className="setting-row__label">Preserve luma</span>{" "}<span data-testid="seg-preserve-luma-help"><InfoHint label="About Preserve luma" text={valueHelpText(SEGMENTATION_HELP.preserve_luma)} /></span>
            </div>
            <div className="setting-row__control">
              <input type="checkbox" data-testid="seg-preserve-luma" checked={c.preserve_luma}
                aria-label="Preserve luma" onChange={(e) => set("preserve_luma", e.target.checked)} />
            </div>
          </div>
        </div>

        {/* Rule 7 — the commit at the bottom of the column, in the SHARED bar, with the
            calibration reset beside it as a secondary. */}
        <ActionBar
          label="AI matte actions"
          primary={
            <button type="button" className="btn btn--primary" disabled={busy}
              data-testid="segmenter-save" onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
          }
        >
          <button type="button" className="btn btn--ghost" disabled={busy}
            data-testid="segmenter-reset" onClick={reset}>
            Reset to defaults
          </button>
          {saved && <span className="faint" data-testid="segmenter-saved">Saved · applied platform-wide.</span>}
        </ActionBar>
      </div>
    </section>
  );
}
